import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { toast } from 'react-toastify';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/AISubscription/LoadingSpinner';
import UsageIndicator from '../components/AISubscription/UsageIndicator';
import { FaCrown, FaCalendar, FaCreditCard, FaChartBar, FaHistory } from 'react-icons/fa';

/**
 * My Subscription Page
 * Manage current subscription, view usage, and billing history
 * Follows Single Responsibility Principle - manages subscription details and actions
 */
const MySubscription = () => {
  const navigate = useNavigate();
  const {
    subscription,
    usageStats,
    loading,
    getTierInfo,
    cancelSubscription,
    fetchCurrentSubscription,
  } = useSubscription();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCurrentSubscription();
  }, [fetchCurrentSubscription]);

  const handleCancelSubscription = async () => {
    setCancelling(true);

    try {
      const result = await cancelSubscription();
      if (result.success) {
        toast.success('Subscription cancelled. Access continues until end of billing period.');
        setShowCancelModal(false);
        fetchCurrentSubscription();
      } else {
        toast.error(result.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div>
        <PublicHeader />
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="lg" text="Loading subscription details..." />
        </div>
        <Footer />
      </div>
    );
  }

  const tierInfo = getTierInfo();
  const isFreeTier = !subscription || tierInfo.name === 'Free';

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Calculate days remaining
  const daysRemaining = subscription?.end_date
    ? Math.ceil((new Date(subscription.end_date) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />

      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            My Subscription
          </h1>
          <p className="text-gray-600">
            Manage your plan, view usage, and billing details
          </p>
        </div>

        {isFreeTier ? (
          // Free Tier - Upgrade Prompt
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white text-center mb-8">
            <FaCrown className="text-6xl mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-2">You're on the Free Plan</h2>
            <p className="text-purple-100 mb-6">
              Upgrade to unlock unlimited AI features and advanced tools
            </p>
            <button
              onClick={() => navigate('/subscription/plans')}
              className="bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              View Plans & Upgrade
            </button>
          </div>
        ) : (
          // Active Subscription Details
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Current Plan Card */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <FaCrown className="text-3xl text-purple-600" />
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {tierInfo.name} Plan
                    </h2>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        tierInfo.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {tierInfo.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/subscription/plans')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors duration-200"
                >
                  Upgrade Plan
                </button>
              </div>

              {/* Subscription Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="flex items-start gap-3">
                  <FaCalendar className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Start Date</p>
                    <p className="font-semibold text-gray-900">
                      {formatDate(subscription?.start_date)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FaCalendar className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">
                      {subscription?.status === 'cancelled' ? 'Access Until' : 'Renews On'}
                    </p>
                    <p className="font-semibold text-gray-900">
                      {formatDate(subscription?.end_date)}
                    </p>
                    {daysRemaining > 0 && daysRemaining <= 7 && (
                      <p className="text-sm text-orange-600">
                        {daysRemaining} days remaining
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FaCreditCard className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Billing Cycle</p>
                    <p className="font-semibold text-gray-900">
                      {subscription?.billing_cycle === 'annual' ? 'Annual' : 'Monthly'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FaCreditCard className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Amount</p>
                    <p className="font-semibold text-gray-900">
                      ₹{subscription?.amount || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              {subscription?.status !== 'cancelled' && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="text-red-600 hover:text-red-700 font-semibold"
                  >
                    Cancel Subscription
                  </button>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaChartBar className="text-blue-600" />
                Quick Stats
              </h3>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Days Active</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {subscription?.start_date
                      ? Math.floor((new Date() - new Date(subscription.start_date)) / (1000 * 60 * 60 * 24))
                      : 0}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Study Streak</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {usageStats?.streak || 0} 🔥
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Total AI Queries</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {(usageStats?.quiz_used || 0) + (usageStats?.doubt_used || 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Usage Statistics */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <FaChartBar className="text-blue-600" />
            Usage This Month
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { feature: 'Quizzes', used: usageStats?.quiz_used || 0, limit: usageStats?.quiz_limit || 0 },
              { feature: 'Doubts', used: usageStats?.doubt_used || 0, limit: usageStats?.doubt_limit || 0 },
              { feature: 'Summaries', used: usageStats?.summary_used || 0, limit: usageStats?.summary_limit || 0 },
              { feature: 'Flashcards', used: usageStats?.flashcard_used || 0, limit: usageStats?.flashcard_limit || 0 },
              { feature: 'Study Plans', used: usageStats?.study_plan_used || 0, limit: usageStats?.study_plan_limit || 0 },
              { feature: 'Mock Tests', used: usageStats?.mock_test_used || 0, limit: usageStats?.mock_test_limit || 0 },
            ].map((usage, index) => (
              <UsageIndicator
                key={index}
                usage={usage}
                showWarning={!isFreeTier}
                onUpgrade={() => navigate('/subscription/plans')}
              />
            ))}
          </div>
        </div>

        {/* Billing History */}
        {!isFreeTier && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <FaHistory className="text-blue-600" />
              Billing History
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {subscription && (
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(subscription.start_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {tierInfo.name} Plan - {subscription.billing_cycle === 'annual' ? 'Annual' : 'Monthly'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ₹{subscription.amount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Paid
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Cancel Subscription?
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to cancel your subscription? You'll continue to have access until{' '}
              {formatDate(subscription?.end_date)}.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={cancelling}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-wait"
              >
                {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MySubscription;
